import { useQuery } from '@apollo/client';
import { useContext } from 'react';
import { getOrganizationProfileByUserIdQuery } from '~/jobiqo/components/Organization/queries/getOragizationQuery';
import { UserContext } from '~/jobiqo/context/user';
import JobTypeEnum from '~/jobiqo/enums/jobType';
import { EntityGroupConfig } from '~/jobiqo/models/config';
import { getGroup } from '~/jobiqo/utils/config';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

/**
 * Returns the job form field groups.
 * If the user has a published organization profile, remove the company_info field
 * group. If the scheduled job feature is disabled, remove the job_schedule field
 * group. If the job type is JOB_PER_FILE, remove the description field.
 * @param {string} jobType - string
 * @returns An array of objects.
 */
export default function useJobFormFieldGroups(jobType: string) {
  const { JOBS } = useFrontendConfig();
  let fieldGroups = Object.values(JOBS.JOB_DETAIL_FIELDS).map((groupInfo) =>
    getGroup(groupInfo as EntityGroupConfig),
  );

  const { user } = useContext(UserContext);

  const { data: organizationProfileData } = useQuery(
    getOrganizationProfileByUserIdQuery(),
    {
      variables: {
        id: user?.id,
      },
      skip: !user?.id,
    },
  );

  // Don't display company_info if an organization profile is published.
  // The job will reference the organization profile.
  if (
    organizationProfileData?.organizationProfileByUserId?.content?.workflowState
      .value === 'published'
  ) {
    fieldGroups = fieldGroups.filter((group) => group.key !== 'company_info');
  }

  // If the flag is disabled, remove the form group.
  if (!JOBS.SCHEDULED_JOB_FEATURE_ACTIVE) {
    fieldGroups = fieldGroups.filter((group) => group.key !== 'job_schedule');
  }

  // If the job type is JOB_PER_FILE, remove the description field.
  return fieldGroups.map((group) => {
    return removeDescriptionField(group, jobType);
  });
}

/**
 * If the job type is JOB_PER_FILE, remove the description field from the group.
 *
 * @param group - The group of fields that you want to modify.
 * @param jobType - The job type.
 * @returns The group object with the field_job_description field removed.
 */
function removeDescriptionField(group, jobType) {
  if (jobType === JobTypeEnum.JOB_PER_FILE) {
    group.fields = group.fields.filter(
      (field) => field.id !== 'field_job_description',
    );
  }

  return group;
}
